/**
 * Utilidades para comparación de priors (P-COMPARE-PRIORS).
 * Merge de stacks de dos estudios en el store sin reemplazar el actual.
 * Agrupación Explorer: StudyInstanceUID + SeriesInstanceUID (no date/SN solos).
 */

import { groupBy } from '../functions'

/**
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function studyUidFromFile(file) {
  if (!file || typeof file !== 'object') return ''
  const st = file.study || {}
  return String(
    st.studyId ||
      st.studyInstanceUID ||
      file.studyInstanceUID ||
      file.study_uid ||
      ''
  ).trim()
}

/**
 * Clave estable de estudio para Explorer (evita fusionar priors por fecha).
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function studyGroupKey(file) {
  const uid = studyUidFromFile(file)
  if (uid) return `uid:${uid}`
  const st = file && file.study ? file.study : {}
  const dt =
    st.studyDateTime != null ? String(st.studyDateTime).trim() : ''
  if (dt) return `dt:${dt}`
  const date = st.studyDate != null ? String(st.studyDate).trim() : ''
  if (date) return `d:${date}`
  const desc =
    st.studyDescription != null ? String(st.studyDescription).trim() : ''
  if (desc) return `desc:${desc}`
  return 'unknown'
}

/**
 * Clave estable de serie (SeriesInstanceUID; fallback SN scoped por StudyUID).
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function seriesGroupKey(file) {
  if (!file || typeof file !== 'object') return 'unknown'
  const su =
    (file.series && file.series.seriesInstanceUID) ||
    file.seriesInstanceUID ||
    ''
  const s = String(su).trim()
  if (s) return `suid:${s}`
  const sn =
    file.series && file.series.seriesNumber != null
      ? file.series.seriesNumber
      : 0
  const stu = studyUidFromFile(file) || 'x'
  return `sn:${stu}:${sn}`
}

/**
 * @param {string|null|undefined} dcmDate YYYYMMDD
 * @returns {string}
 */
export function formatStudyDateLabel(dcmDate) {
  const s = String(dcmDate || '')
  if (s.length >= 8) {
    return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`
  }
  return s || ''
}

/**
 * Conteos de series/imágenes de un bucket de estudio.
 * @param {object[]|null|undefined} files
 * @returns {{ seriesCount: number, instanceCount: number, series: { key: string, count: number, seriesNumber: *, description: string }[] }}
 */
export function studySeriesStats(files) {
  const list = Array.isArray(files) ? files.filter(Boolean) : []
  if (!list.length) {
    return { seriesCount: 0, instanceCount: 0, series: [] }
  }
  const { seriesList, seriesKeys } = buildSeriesMapForStudy(list)
  const series = seriesKeys.map((k) => {
    const arr = seriesList.get(k) || []
    const first = arr[0] || {}
    const sn =
      first.series && first.series.seriesNumber != null
        ? first.series.seriesNumber
        : null
    const description =
      (first.series && first.series.seriesDescription) || ''
    return {
      key: k,
      count: arr.length,
      seriesNumber: sn,
      description: String(description || '').trim(),
    }
  })
  return {
    seriesCount: seriesKeys.length,
    instanceCount: list.length,
    series,
  }
}

/**
 * @param {{ seriesCount?: number, instanceCount?: number, series?: { count?: number }[] }|null|undefined} stats
 * @returns {string}
 */
export function formatStudyStatsLine(stats) {
  if (!stats) return ''
  const sc = Number(stats.seriesCount) || 0
  const ic = Number(stats.instanceCount) || 0
  const counts = Array.isArray(stats.series)
    ? stats.series.map((s) => s && s.count).filter((n) => n > 0)
    : []
  if (sc > 0 && counts.length > 0) {
    return `${sc} series (${counts.join(', ')})`
  }
  if (sc > 0 && ic > 0) return `${sc} series · ${ic} img`
  if (ic > 0) return `${ic} img`
  if (sc > 0) return `${sc} series`
  return ''
}

/**
 * Label legible para el dropdown Study (value = studyGroupKey).
 * @param {object[]|null|undefined} files
 * @param {{ inViewport?: boolean }} [opts]
 * @returns {string}
 */
export function studyDropdownLabel(files, opts = {}) {
  const list = Array.isArray(files) ? files : []
  const f = list.find(Boolean) || {}
  const st = f.study || {}
  const date =
    formatStudyDateLabel(st.studyDate) ||
    (st.studyDateTime != null
      ? String(st.studyDateTime).trim().slice(0, 16)
      : '')
  const mod =
    st.modalitiesInStudy ||
    (f.series && f.series.modality) ||
    f.modality ||
    ''
  const desc = st.studyDescription ? String(st.studyDescription).trim() : ''
  const parts = [date, mod, desc].filter(Boolean)
  const statsLine = formatStudyStatsLine(studySeriesStats(list))
  if (statsLine) parts.push(statsLine)
  if (opts && opts.inViewport) parts.push('en vista')
  if (parts.length) return parts.join(' · ')
  const uid = studyUidFromFile(f)
  return uid ? `Estudio ${uid.slice(0, 12)}…` : 'Estudio'
}

/**
 * @param {Map<string, object[]>} studyList
 * @returns {string[]}
 */
export function sortStudyKeys(studyList) {
  const keys = [...(studyList || new Map()).keys()].filter(
    (k) => k != null && String(k) !== ''
  )
  keys.sort((a, b) => {
    const fa = (studyList.get(a) || [])[0]
    const fb = (studyList.get(b) || [])[0]
    const da = (fa && fa.study && fa.study.studyDate) || ''
    const db = (fb && fb.study && fb.study.studyDate) || ''
    if (da !== db) return String(db).localeCompare(String(da))
    return String(a).localeCompare(String(b))
  })
  return keys
}

/**
 * @param {Map<string, object[]>} seriesList
 * @returns {string[]}
 */
function isReportSeriesFile(file) {
  if (!file || typeof file !== 'object') return false
  const role = file.role || (file.series && file.series.role)
  if (role === 'report') return true
  const mod = String(
    (file.series && (file.series.modality || file.series.Modality)) ||
      file.modality ||
      ''
  )
    .trim()
    .toUpperCase()
  return mod === 'DOC' || mod === 'PDF'
}

export function sortSeriesKeys(seriesList) {
  const keys = [...(seriesList || new Map()).keys()]
  keys.sort((a, b) => {
    const fa = (seriesList.get(a) || [])[0]
    const fb = (seriesList.get(b) || [])[0]
    const ra = isReportSeriesFile(fa)
    const rb = isReportSeriesFile(fb)
    if (ra !== rb) return ra ? 1 : -1
    const sna =
      fa && fa.series && fa.series.seriesNumber != null
        ? Number(fa.series.seriesNumber)
        : 0
    const snb =
      fb && fb.series && fb.series.seriesNumber != null
        ? Number(fb.series.seriesNumber)
        : 0
    if (sna !== snb) return sna - snb
    return String(a).localeCompare(String(b))
  })
  return keys
}

/**
 * Mapas study/series para un paciente (keys UID-safe).
 * @param {object[]} patientFiles
 * @returns {{ studyList: Map, studyKeys: string[], studyLabels: Object<string,string> }}
 */
export function buildStudySeriesMaps(patientFiles) {
  const studyList = groupBy(patientFiles || [], studyGroupKey)
  const studyKeys = sortStudyKeys(studyList)
  const studyLabels = {}
  for (let i = 0; i < studyKeys.length; i++) {
    const k = studyKeys[i]
    studyLabels[k] = studyDropdownLabel(studyList.get(k))
  }
  return { studyList, studyKeys, studyLabels }
}

/**
 * Series de un study bucket, ordenadas por SeriesNumber.
 * @param {object[]} studyFiles
 * @returns {{ seriesList: Map, seriesKeys: string[] }}
 */
export function buildSeriesMapForStudy(studyFiles) {
  const raw = groupBy(studyFiles || [], seriesGroupKey)
  const seriesKeys = sortSeriesKeys(raw)
  const seriesList = new Map()
  for (let i = 0; i < seriesKeys.length; i++) {
    const k = seriesKeys[i]
    seriesList.set(k, raw.get(k) || [])
  }
  return { seriesList, seriesKeys }
}

/**
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function fileInstanceKey(file) {
  if (!file || typeof file !== 'object') return ''
  const sop =
    file.sopInstanceUID != null ? String(file.sopInstanceUID).trim() : ''
  if (sop) return `sop:${sop}`
  const imageId = file.imageId != null ? String(file.imageId).trim() : ''
  if (imageId) return `img:${imageId}`
  return ''
}

/**
 * Concatena base + incoming sin duplicar instancias.
 * @param {object[]} base
 * @param {object[]} incoming
 * @returns {object[]}
 */
export function mergeStudyFileLists(base, incoming) {
  const out = []
  const seen = new Set()
  const push = (f) => {
    if (!f) return
    const k = fileInstanceKey(f)
    if (k) {
      if (seen.has(k)) return
      seen.add(k)
    }
    out.push(f)
  }
  const a = Array.isArray(base) ? base : []
  const b = Array.isArray(incoming) ? incoming : []
  for (let i = 0; i < a.length; i++) push(a[i])
  for (let j = 0; j < b.length; j++) push(b[j])
  return out
}

/**
 * Explorer data desde lista de files (mismo shape que loadStudyFromManifest).
 * @param {object[]} items
 */
export function buildExplorerDataFromFiles(items) {
  const list = Array.isArray(items) ? items : []
  const patientList = groupBy(list, (a) =>
    a && a.patient && a.patient.patientName
      ? a.patient.patientName
      : 'Unknown'
  )
  return {
    folder: null,
    patient: {
      list: patientList,
      keys: [...patientList.keys()],
    },
  }
}

/**
 * Primera serie “displayable” de un StudyInstanceUID dentro del pool.
 * Agrupa por SeriesInstanceUID (no solo SeriesNumber).
 * @param {object[]} files
 * @param {string} studyUid
 * @returns {{ seriesFiles: object[], explorerHint: number }|null}
 */
export function pickFirstSeriesForStudy(files, studyUid) {
  const want = String(studyUid || '').trim()
  if (!want || !Array.isArray(files) || !files.length) return null
  const ofStudy = files.filter((f) => studyUidFromFile(f) === want)
  if (!ofStudy.length) return null

  const { seriesList, seriesKeys } = buildSeriesMapForStudy(ofStudy)
  for (let i = 0; i < seriesKeys.length; i++) {
    const cand = seriesList.get(seriesKeys[i]) || []
    if (cand.some((f) => f && (f.image || f.imageId))) {
      return { seriesFiles: cand, explorerHint: i }
    }
  }
  const firstKey = seriesKeys[0]
  const first = firstKey != null ? seriesList.get(firstKey) : ofStudy
  return { seriesFiles: first || ofStudy, explorerHint: 0 }
}
